import { Inject, Injectable } from '@angular/core';
import { CompareCartService } from '@cart/data-access';
import { BusinessRegion, BusinessRegionResponse, GeoPointShort } from '@city/const';
import { CityActions, OccBusinessRegionService } from '@city/data-access';
import { DEFAULT_CITY, GeoPoint, Sentry, SENTRY } from '@core/const';
import { EnvironmentService } from '@core/feature';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { GlobalMessageActions, GlobalMessageType } from '@spartacus/core';
import { from, Observable, of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { CityService } from '../services/city.service';

function mapGeoPointFromShort(coords: GeoPointShort): GeoPoint {
  return { latitude: coords.lat, longitude: coords.lon };
}

@Injectable()
export class BusinessRegionEffects {
  loadBusinessRegion$: Observable<
    CityActions.BusinessRegionLoadSuccess | CityActions.BusinessRegionLoadFail | GlobalMessageActions.AddMessage
  > = createEffect(() =>
    this.actions$.pipe(
      ofType(CityActions.LOAD_BUSINESS_REGION),
      map((action: CityActions.BusinessRegionLoad) => action.geoPoint),
      switchMap((geoPoint: GeoPoint) => {
        return this.occBusinessRegionService.loadBusinessRegion(geoPoint).pipe(
          // #region проверка на то что clientPoint является бейсстором
          switchMap((res) => {
            return this.cityService
              .isBaseStore(res.data.clientPoint.slug)
              .pipe(map((isBaseStore) => [isBaseStore, res] as [boolean, BusinessRegionResponse]));
          }),
          tap(([isBaseStore, res]) => {
            if (isBaseStore) {
              return;
            }

            this.cityService.setBaseStore(DEFAULT_CITY);
            const error = new Error(`Попытка установить ${res.data.clientPoint.slug} в качестве базового региона`);
            this.sentry.captureException(error);
            throw error;
          }),
          map(([, res]) => res),
          map<BusinessRegionResponse, CityActions.BusinessRegionLoadSuccess>(
            ({ data: { inDeliveryZone, clientPoint, clientCity } }: BusinessRegionResponse) => {
              const { coords, ...point } = clientPoint;
              const { coords: coordsCity, ...city } =
                clientCity && this.environmentService.isPlatformBrowser
                  ? clientCity
                  : ({} as BusinessRegionResponse['data']['clientCity']);

              return new CityActions.BusinessRegionLoadSuccess({
                inDeliveryZone,
                point: {
                  ...point,
                  geoPoint: mapGeoPointFromShort(coords),
                },
                city: coordsCity
                  ? {
                      ...city,
                      geoPoint: mapGeoPointFromShort(coordsCity),
                    }
                  : undefined,
              });
            },
          ),
          catchError((error: any) => {
            const actions: Array<CityActions.BusinessRegionLoadFail | GlobalMessageActions.AddMessage> = [
              new CityActions.BusinessRegionLoadFail(error),
            ];

            if (error?.error?.errors) {
              error.error.errors.forEach((err: any) => {
                if (err.message) {
                  actions.push(
                    new GlobalMessageActions.AddMessage({
                      text: { raw: err.message },
                      type: GlobalMessageType.MSG_TYPE_ERROR,
                    }),
                  );
                }
              });
            }

            return from(actions);
          }),
        );
      }),
    ),
  );

  loadBusinessRegionSuccess$: Observable<any> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CityActions.LOAD_BUSINESS_REGION_SUCCESS),
        map((action: CityActions.BusinessRegionLoadSuccess) => action.payload),
        tap(({ point: { slug } }: BusinessRegion) => {
          if (this.environmentService.isPlatformServer) {
            this.cityService.setBaseStoreWithoutCompareCart(slug);
          } else {
            this.compareCartService.compareCart(slug);
          }
        }),
      ),
    { dispatch: false },
  );

  loadBusinessRegionForCurrentPoint$: Observable<
    CityActions.LoadBusinessRegionFromCoordsSuccess | CityActions.LoadBusinessRegionFromCoordsFail
  > = createEffect(() =>
    this.actions$.pipe(
      ofType(CityActions.LOAD_BUSINESS_REGION_FROM_COORDS),
      map((action: CityActions.BusinessRegionLoad) => action.geoPoint),
      switchMap((geoPoint: GeoPoint) => {
        return this.occBusinessRegionService.loadBusinessRegion(geoPoint).pipe(
          map((res) => new CityActions.LoadBusinessRegionFromCoordsSuccess(res.data)),
          catchError((error: any) => of(new CityActions.LoadBusinessRegionFromCoordsFail(error))),
        );
      }),
    ),
  );

  constructor(
    private actions$: Actions,
    private cityService: CityService,
    private occBusinessRegionService: OccBusinessRegionService,
    private compareCartService: CompareCartService,
    private environmentService: EnvironmentService,
    @Inject(SENTRY) private sentry: Sentry,
  ) {}
}
